/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import _ from 'lodash';
import moment from 'moment-timezone';

import { statuses } from 'common/utils/constants';
import * as ApiService from 'common/services/ApiService';
import * as PropertyService from 'common/services/PropertyService';
import * as PropertySearchService from 'common/services/PropertySearchService';
import { PROPERTY_STATUS } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import {
    createContainsLoopbackFilter,
    createLoopbackFilter,
} from 'common/components/data-grid/DataGridUtils';

const baseUrl = 'properties';

export function createProperty(data) {
    return ApiService.post(true, 'Properties', data);
}

export function createBulkProperty(data) {
    return ApiService.post(true, 'Properties/bulkCreate', data);
}

export function validateAdvancedAddress(data) {
    return ApiService.post(true, 'Properties/validateAdvancedAddress', data);
}

export function createUnverifiedProperty(data) {
    Object.assign(data, { createUnverifiedProperty: true });
    return ApiService.post(true, baseUrl, data);
}

export function getPropertiesByParent(filterQuery) {
    const filter = {
        filter: filterQuery,
    };
    return PropertyService.getPropertiesByParent(filter);
}

export function getParentProperties(filterQuery) {
    // 07/18/2018 - getParentProperties replaces assetParentProperties in MetaService.
    return PropertyService.getParentProperties({ filter: filterQuery });
    // return ApiService.get(true, 'Properties', {
    //     filter: {
    //         order: ['rawAddress ASC'],
    //         fields: [
    //             'id',
    //             'name',
    //             'rawAddress'
    //         ]
    //     }
    // });
}

export async function getProperties(filterQuery) {
    const filterFields = {
        filter: {
            include: [
                {
                    relation: 'client',
                    scope: {
                        include: ['requirements'],
                    },
                },
                {
                    relation: 'activePropertyProfile',
                    scope: {
                        fields: ['id', 'primaryPreferredContractorId'],
                        include: [
                            {
                                relation: 'primaryPreferredContractor',
                                scope: {
                                    fields: ['name'],
                                },
                            },
                        ],
                    },
                },
            ],
            fields: [
                'id',
                'name',
                'typeId',
                'rawAddress',
                'stdAddress',
                'stdRegion',
                'stdPostalCode',
                'rdi',
                'clientId',
                'primaryPreferredContractorId',
                'propertyProfileId',
                'created',
                'latitude',
                'longitude',
                'statusId',
                'occupied',
                'serviceBranch',
                'serviceRegion',
                'isParent',
                'parentPropertyId',
            ],
        },
    };
    const query = _.merge({}, filterFields, { filter: filterQuery });
    return PropertyService.find(query);
}

export async function getPropertyIdsBySearch(filterQuery) {
    const filterFields = {
        filter: {
            fields: ['id'],
        },
    };
    const query = _.merge({}, filterFields, { filter: filterQuery });
    return PropertyService.find(query);
}

export async function getPropertyIdsByAddress(searchTerm, startsWith = false) {
    const query = {
        filter: {
            fields: ['id'],
            where: {
                stdAddress: {
                    ilike: startsWith ? `${searchTerm}%` : `%${searchTerm}%`,
                },
            },
        },
    };
    return PropertyService.find(query);
}

export function propertySearch(filterQuery) {
    const query = {
        filter: filterQuery,
    };
    return PropertySearchService.propertySearch(query);
}

export async function getAdminClientProperties(filterQuery) {
    const filterFields = {
        filter: {
            fields: [
                'id',
                'name',
                'typeId',
                'standardized',
                'stdAddress',
                'stdDeliveryLine',
                'stdLastLine',
                'stdPrimaryNumber',
                'stdStreetPreDirection',
                'stdStreetName',
                'stdStreetSuffix',
                'stdStreetPostDirection',
                'stdSecondaryDesignator',
                'stdSecondaryNumber',
                'stdCity',
                'stdRegion',
                'stdPostalCode',
                'stdPostalCodePlus4',
                'created',
                'statusId',
                'isParent',
                'parentPropertyId',
                'timezone',
            ],
        },
    };
    const query = _.merge({}, filterFields, { filter: filterQuery });
    return PropertyService.find(query);
}

/**
 * @todo move this to meta service/store, this is now in metastore but propertyCreate needs to be changed
 */
export function getPropertyTypes() {
    return ApiService.get(true, 'Properties/types').then(res =>
        res.map(item => ({ display: item.display, term: item.id }))
    );
}

export async function processFilters(activeFilters, activeQuery) {
    const query = _.merge({}, activeQuery, {
        where: {
            and: [],
        },
    });

    if (!activeFilters.length) {
        return query;
    }

    const stdRegions = [];
    const stdCountrys = [];
    const typeFilter = { or: [] };
    const statusFilter = { or: [] };

    activeFilters.forEach(filter => {
        switch (filter.target) {
            // Search Filters
            case 'ADDRESS':
                query.where.and.push(createLoopbackFilter(filter).where);
                break;
            case 'NAME':
                query.where.and.push(createLoopbackFilter(filter).where);
                break;
            case 'CLIENT':
                query.where.and.push(createLoopbackFilter(filter).where);
                break;
            case 'CONTRACTOR':
                query.where.and.push(createLoopbackFilter(filter).where);
                break;
            case 'ZIP':
                query.where.and.push(createLoopbackFilter(filter).where);
                break;

            // Advanced Filters
            case 'createdBefore':
                query.where.and.push({
                    created: {
                        lt: moment(filter.term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'createdAfter':
                query.where.and.push({
                    created: {
                        gt: moment(filter.term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'type':
                typeFilter.or.push({ typeId: filter.term });
                break;
            case 'isParent':
                if (filter.term === true) {
                    query.where.and.push({ isParent: true });
                }
                break;
            case 'state':
                stdRegions.push(filter.term);
                break;
            case 'country':
                stdCountrys.push(filter.term);
                break;
            case 'zip':
                query.where.and.push({ id: { inq: filter.term } });
                break;
            case 'status':
                statusFilter.or.push({ statusId: filter.term });
                break;
            default:
        }
    });

    if (stdRegions.length > 0) {
        query.where.and.push({ stdRegion: { inq: stdRegions } });
    }
    if (stdCountrys.length > 0) {
        query.where.and.push({ stdCountry: { inq: stdCountrys } });
    }

    query.where.and.push(typeFilter, statusFilter);

    return query;
}

/**
 * returns html string for use with google maps info window
 * @param {object} property
 * @param {number} property.id
 * @param {string} property.rawAddress
 * @param {string} [property.stdAddress]
 * @param {string} property.typeId
 * @param {object} client
 * @param {string} client.name
 * @param {string} [client.doingBusinessAs]
 * @param {boolean} [showEditProperty]
 * @returns {string}
 */
export function ExistingPropertyInfoWindow(
    property,
    client,
    propertyTypes,
    showEditProperty = true
) {
    const propertyTypeDisplay = propertyTypes?.find(
        type => type.id === property?.typeId
    );

    return `
        <div style="font-weight: bold">
            ${client.name}${
                client.doingBusinessAs && client.doingBusinessAs !== client.name
                    ? ` (${client.doingBusinessAs})`
                    : ''
            } Property #${property?.id}
        </div>
        <div>${property?.stdAddress || property?.rawAddress}</div>
        <div>Type: ${propertyTypeDisplay?.display}</div>
        ${
            showEditProperty
                ? `<a href="#/ops/properties/${property?.id}" target="_blank">Edit Property</a>`
                : ''
        }
    `;
}

export function displayPropertyInfoWindow(address, timezone) {
    return `
        <div style="font-weight: bold">${address}</div>
        ${timezone ? `<div>${timezone}</div>` : ''}
    `;
}

/**
 * returns html string for use with google maps info window
 * @param {string} address
 * @param {string} timezone
 * @returns {string}
 */
export function createPropertyInfoWindow(address, timezone) {
    const createPropertyParam = new URLSearchParams();
    createPropertyParam.set('newAddress', address);
    const _href = `#/ops/properties/create?${createPropertyParam.toString()}`;
    return `
            <div style="font-weight: bold">${address}</div>
            ${timezone ? `<div>${timezone}</div>` : ''}
            <div>This property has not been created. <a id="property-create-link" href=${_href} target="_blank" rel="noopener noreferrer">Click here</a> to create.</div>

    `;
}

export function getPropertyChecklistRequirements(propertyId) {
    const query = {
        filter: {
            include: [
                {
                    relation: 'client',
                    scope: {
                        fields: [
                            'id',
                            'name',
                            'statusId',
                            'doingBusinessAs',
                            'operationInCountry',
                        ],
                        include: [
                            {
                                relation: 'requirements',
                                scope: {
                                    where: {
                                        requirementTypeId: 'CHECKLIST',
                                    },
                                },
                            },
                        ],
                    },
                },
            ],
            fields: [
                'id',
                'name',
                'typeId',
                'statusId',
                'categorization',
                'stdAddress',
                'stdDeliveryLine',
                'rawAddress',
                'stdRegion',
                'stdCity',
                'stdPostalCode',
                'clientId',
                'created',
                'latitude',
                'longitude',
                'timezone',
            ],
        },
    };
    return PropertyService.findByIdWithPreferredContractor(propertyId, query);
}

export function getProperty(id) {
    const query = {
        filter: {
            include: [
                {
                    relation: 'client',
                    scope: {
                        fields: [
                            'name',
                            'statusId',
                            'doingBusinessAs',
                            'operationInCountry',
                            'enabledVideoRecording',
                        ],
                        include: [
                            { relation: 'requirements' },
                            { relation: 'blacklists' },
                        ],
                    },
                },
                {
                    relation: 'activePropertyProfile',
                    scope: {
                        include: [
                            {
                                relation: 'primaryPreferredContractor',
                                scope: {
                                    fields: ['id', 'name'],
                                },
                            },
                            {
                                relation: 'secondaryPreferredContractor',
                                scope: {
                                    fields: ['id', 'name'],
                                },
                            },
                            {
                                relation: 'preferredContractor3',
                                scope: {
                                    fields: ['id', 'name'],
                                },
                            },
                            {
                                relation: 'preferredContractor4',
                                scope: {
                                    fields: ['id', 'name'],
                                },
                            },
                        ],
                    },
                },
                {
                    relation: 'parent',
                },
                {
                    relation: 'children',
                },
                {
                    relation: 'workOrders',
                    scope: {
                        fields: ['id', 'number'],
                        order: 'id DESC',
                        limit: 1,
                    },
                },
                {
                    relation: 'histories',
                },
            ],
        },
    };
    return PropertyService.findByIdWithPreferredContractor(id, query);
}

export function createNote(id, note, mentionsData) {
    const _note = _.assign(
        {},
        {
            message: note.note,
            mentionsData,
        }
    );
    return PropertyService.createNote(id, _note);
}

export function updateNote(id, note, mentionsData) {
    const _note = _.assign(
        {},
        {
            message: note.message,
            mentionsData,
        }
    );
    return PropertyService.updateNote(id, note.id, _note);
}

/**
 * Upload property from list
 * @param {*} data
 * @returns {Promise}
 */
export function bulkUpload(
    clientId,
    header,
    properties,
    withParent,
    bypassAddressVerification
) {
    return ApiService.post(
        true,
        `${baseUrl}/batch/${clientId}`,
        properties.map(property => _.zipObject(header, property)),
        { withParent, bypassAddressVerification }
    );
}

/**
 * Update property detail
 * @param {*} propertyId
 * @param {*} data
 * @returns {Promise}
 */
export function updateDetail(propertyId, data) {
    return ApiService.patch(true, `${baseUrl}/${propertyId}`, data);
}

export function getFileInformation(propertyId) {
    const query = {
        filter: {
            include: [
                {
                    relation: 'folder',
                    scope: {
                        include: {
                            relation: 'folderAttachments',
                            scope: {
                                include: {
                                    relation: 'uploadedBy',
                                },
                            },
                        },
                    },
                },
            ],
        },
    };
    return PropertyService.findById(propertyId, query);
}

export function findUnitsByProperty(property) {
    return ApiService.get(true, `Properties/tree/${property.id}`);
}

export function getSuggestParentPropertyByClientId(clientId) {
    return ApiService.get(true, 'Properties', {
        filter: {
            where: {
                and: [
                    { clientId },
                    { isParent: true },
                    { statusId: { inq: [statuses.ACTIVE] } },
                ],
            },
            order: ['rawAddress ASC'],
            fields: [
                'id',
                'name',
                'rawAddress',
                'stdPrimaryNumber',
                'stdCity',
                'stdRegion',
                'stdPostalCode',
                'stdStreetPreDirection',
                'stdStreetName',
                'stdStreetSuffix',
                'stdStreetPostDirection',
                'stdLastLine',
            ],
        },
    });
}

export async function getSuggestParentPropertyByClientIdExcludeSelfAndChild(
    clientId,
    property
) {
    const properties = await getSuggestParentPropertyByClientId(clientId);
    const tree = await findUnitsByProperty(property);
    const _property = _findPropertyInTree(tree, property.id);
    if (_property && properties) {
        const targetParentProperties = properties.filter(
            p => !_findPropertyInTree(_property, p.id)
        );
        // We need parent which has same address.
        if (targetParentProperties && targetParentProperties.length > 0) {
            return targetParentProperties.filter(
                p =>
                    property.stdPrimaryNumber === p.stdPrimaryNumber &&
                    property.stdStreetPreDirection ===
                        p.stdStreetPreDirection &&
                    property.stdStreetName === p.stdStreetName &&
                    property.stdStreetSuffix === p.stdStreetSuffix &&
                    property.stdStreetPostDirection ===
                        p.stdStreetPostDirection &&
                    property.stdCity === p.stdCity &&
                    property.stdRegion === p.stdRegion &&
                    property.stdPostalCode === p.stdPostalCode
            );
        }
        return [];
    }
    return properties;
}

/**
 * Search for properties within specified distance of specified zip
 *
 * @param query LoopBack filter
 * @returns {Promise<Array>}
 */
export function getPropertiesNearZip(query) {
    return ApiService.get(true, 'Properties/findDistZip', { filter: query });
}

/**
 *
 * @param {number} id { property id }
 * @param {object} filter optional
 * @returns {Promise}
 */
export function getpropertyAssets(id, filter = { filter: {} }) {
    return ApiService.get(true, `Properties/${id}/assets`, filter);
}

/**
 *
 * @param id
 * @param data
 * @return {Promise}
 */
export function deactivate(id, data) {
    return ApiService.post(true, `Properties/${id}/deactivate`, data);
}

/**
 *
 * @param id
 * @return {Promise}
 */
export function validateDeactivate(id) {
    return ApiService.get(true, `Properties/${id}/validateDeactivate`);
}

/**
 * Validate that is it still have open document for properties
 * @param {number[]} ids - list of property id
 * @returns {Promise<object>}
 */
export function validateOpenDocuments(ids) {
    return ApiService.post(true, `Properties/validateOpenDocuments`, ids);
}

/**
 *
 * @param id
 * @param data
 * @return {Promise}
 */
export function activate(id, data) {
    return ApiService.post(true, `Properties/${id}/activate`, data);
}
/**
 *
 * @param ids
 * @param data
 * @return {Promise}
 */
export function activateProperties(ids, data) {
    return ApiService.post(true, `Properties/activateProperties`, data, {
        ids,
    });
}

/**
 *
 * @param ids
 * @param data
 * @return {Promise}
 */
export function deactivateProperties(ids, data) {
    return ApiService.post(true, `Properties/deactivateProperties`, data, {
        ids,
    });
}

function _findPropertyInTree(tree, id) {
    if (!tree) return null;
    if (tree.id === id) return tree;
    if (Array.isArray(tree.children)) {
        let i;
        let result = null;
        for (i = 0; !result && i < tree.children.length; i++) {
            result = _findPropertyInTree(tree.children[i], id);
        }
        return result;
    }
    return null;
}

/**
 * This function will get all properties of the specific client
 * and will be use to displaying a property tree in client detail tab
 * @param {number} clientId
 * @returns {Promise<[]>}
 */
export function getClientProperties(clientId) {
    const query = {
        filter: {
            fields: [
                'id',
                'name',
                'typeId',
                'standardized',
                'stdAddress',
                'stdDeliveryLine',
                'stdLastLine',
                'stdPrimaryNumber',
                'stdStreetPreDirection',
                'stdStreetName',
                'stdStreetSuffix',
                'stdStreetPostDirection',
                'stdSecondaryDesignator',
                'stdSecondaryNumber',
                'stdCity',
                'stdRegion',
                'stdPostalCode',
                'stdPostalCodePlus4',
                'stdCountry',
                'created',
                'statusId',
                'isParent',
                'parentPropertyId',
                'timezone',
                'clientId',
                'numberOfSystem',
            ],
            where: { clientId },
        },
    };
    return PropertyService.find(query);
}

export function getClientPropertiesWithCount(
    activeFilters,
    activeQuery,
    clientId
) {
    const filterQuery = _.merge({}, activeQuery, {
        where: {
            and: [{ clientId }],
        },
    });

    Object.keys(activeFilters).forEach(key => {
        switch (key) {
            case 'address':
                filterQuery.where.and.push(
                    createContainsLoopbackFilter(
                        'stdAddress',
                        activeFilters[key].term
                    ).where
                );
                break;
            case 'typeId':
                if (activeFilters[key].term !== 'NONE') {
                    filterQuery.where.and.push({
                        typeId: activeFilters[key].term,
                    });
                }
                break;
            case 'hideParent':
                if (activeFilters[key].term) {
                    filterQuery.where.and.push({
                        or: [
                            { isParent: false },
                            { typeId: { neq: 'APARTMENT' } },
                        ],
                    });
                }
                break;
            case 'statusId':
                if (activeFilters[key].term) {
                    filterQuery.where.and.push({
                        statusId: PROPERTY_STATUS.ACTIVE,
                    });
                }
                break;
            default:
        }
    });

    return propertySearch({
        ...filterQuery,
        fields: [
            'id',
            'clientId',
            'name',
            'typeId',
            'stdAddress',
            'stdRegion',
            'stdPostalCode',
            'stdPostalCode',
            'stdCountry',
            'created',
            'statusId',
            'isParent',
            'numberOfSystem',
        ],
    });
}

export async function getActiveClientPropertyOptions(clientId) {
    if (!clientId) {
        return [];
    }
    const properties = await getAdminClientProperties({
        where: { clientId, statusId: PROPERTY_STATUS.ACTIVE },
    });
    return properties.map(p => ({
        id: p.id,
        display:
            [p.stdDeliveryLine, p.stdLastLine].filter(Boolean).join(', ') ||
            p.name ||
            p.stdAddress ||
            `Property #${p.id}`,
    }));
}
